import { useCallback, useEffect, useState, useMemo } from "react";
import { P2Alert } from "react-bootstrap-common-script";
import { useAppDispatch, useAppSelector } from "../redux/hook";
import { useGetOngoingApplicationQuery } from "../redux/services/getOngoingApplicationsStatus";
import { StatusUpdateInterface } from "../models/interfaces/update-status.interface";
import { StagesStatusEnum } from "../models/enum/stages-status.enum";
import { Helpers } from "../utils/helper";
import { PolymerAxios } from "../utils/axios";
import { API_ApplicationDetail, API_DraftDetail, API_HLEStatus, API_UpdateNotification } from "../utils/api";
import { useMapDraft } from "./use-map-draft";
import { useNavigate } from "react-router-dom";
import { ApplicationStatusEnum, PolymerStatusEnum } from "../models/enum/application-status.enum";
import { setApplicationId } from "../redux/slices/application-id-slice";
import { resetAuthData } from "../redux/slices/authentication-slice";
import { PolymerCrypto } from "../utils/encryptor";
import moment from "moment";
import expiryDuration from "../config/expiry_duration";
import { useGetStageD1ObjQuery } from "../redux/services/getStageD";
import { openRouteStageD } from "../redux/slices/routes-management-slice";
import { cloneDeep } from "lodash";
import { setApplicationDetails, setCancelledBy } from "../redux/slices/application-cancelled-slice";
import { Properties } from "../models/polymer/Properties.model";


type DashboardCode = "UNDraft" | "UNHLEDraft" | "UNCNoSubmission" | "UNEndorsement" | "UNREndorsement" | "UNCIncompleteEndorse" | "UNPayment" | "UNRPayment" | "UNCNoPayment" | "UNCNoDocument" | "UNRequestDocument" | "UNRRequestDocument" | "UNExtendDue" | "UNRExtendDue" | "UNLawyer" | "UNR2Lawyer" | "UNCApplicant" | "UNCHDB" | "UNReject" | "UNApprove" | "Multiple" | null;

interface GetDashboardCodeProps{
  code:DashboardCode;
  application_id: string;
  application_create_date: string;
  last_updated_at: string;
  gather_consent_complete_date: string
  payment_date: string,
  alert_code: string
  updated_by: string
  cancellation_reason: string,
  due_date: string,
  request_date: string
}

const useGetDashboardAlert = () => {
  

  const navigate = useNavigate();
  const AppDispatch = useAppDispatch();
  const { performDraftMapping, generatedDraftObj } = useMapDraft();
  const loginUser = useAppSelector(state => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);
  const properties = useAppSelector(state => state.app.GenuinePolymerSlice.properties);
  const [ alertJSX, setAlertJSX ] = useState<JSX.Element>(<></>);
  const [ isOneTimeDisplay, setIsOneTimeDisplay ] = useState<boolean>(false);
  
  const [ applicationDetail, setApplicationDetail ] = useState<any>();
  const [ hasHLE, setHasHLE ] = useState<boolean | undefined>(undefined);
  

  const { data: ongoingApplications, refetch, currentData} = useGetOngoingApplicationQuery({uinfin:loginUser?.uinfin ?? "", singpass_token: singpassToken ?? ""});
  const wantedStatus = [
    "SAVE_DRAFT",
    "SUBMIT_COMPLETE",
    "GATHER_CONSENT_COMPLETE",
    "PENDING_PAYMENT",
    "PAYMENT_COMPLETE",
    "SENT_TO_BIOME",
    "PENDING_APPLICANT_HDB_REVIEW",
    "HDB_REVIEW",
    "APPROVE",
    "PENDING_BOOKING_APPOINTMENT",
    "REVIEW_FINANCIAL_PLAN",
    "IPA_REVIEW_COMPLETE",
    "PREPARE_FOR_COMPLETION",
  ];
  const sortedOngoingApplications = useMemo(() => {
    const clonedOngoingApplications = cloneDeep(ongoingApplications);
  
    const activeApplications = clonedOngoingApplications?.filter(app =>
      wantedStatus.includes(app.application_status)
    );
  
    return activeApplications;
  }, [ongoingApplications]);

  const sortedAllApplications = useMemo(() => {
    const clonedOngoingApplications = cloneDeep(ongoingApplications);
  
    const activeApplications = clonedOngoingApplications?.filter(app =>
      wantedStatus.includes(app.application_status)
    );

    const nonActiveApplications = clonedOngoingApplications?.filter(app =>
      !wantedStatus.includes(app.application_status)
    );
  
    activeApplications?.sort((a, b) => (b.last_updated_date ?? "").localeCompare(a.last_updated_date ?? ""));
    nonActiveApplications?.sort((a, b) => (b.last_updated_date ?? "").localeCompare(a.last_updated_date ?? ""));
  
    return activeApplications?.concat(nonActiveApplications ?? []);
  }, [ongoingApplications]);
    

  const firstApplicationStatus = useMemo(()=>{
    if(sortedOngoingApplications && sortedOngoingApplications?.length > 0){
      return sortedOngoingApplications[0];
    }
    else{
      return null;
    }
  },[sortedOngoingApplications])


  const application_id = useMemo(()=>{
    if(firstApplicationStatus){
      return firstApplicationStatus.application_id;
    }
    else{
      return "0";
    }
  },[firstApplicationStatus])
  
  const { data:StageDData } = useGetStageD1ObjQuery({ application_uuid: application_id ?? "", uinfin: loginUser?.uinfin ?? "", stage: "D1", singpass_token: singpassToken ?? "" },{ skip: !application_id || application_id === "0" });

  const stageDRequestedDate = useMemo(()=>{
    const receivedDatesArr = StageDData?.message_info.map(e=>e.received_date);
    try{
      if(receivedDatesArr && receivedDatesArr?.length > 0){
        return Helpers.formatDate(receivedDatesArr[0]);
      }
    }
    catch(e){
      console.log("DEBUG -- DASHBOARD REQUEST DATE", e);
      return "";
    }
  },[StageDData?.message_info])

  useEffect(() => {
    if(currentData !== undefined){
       refetch();
    }
  }, [currentData, refetch])


  //DONE
  const checkHLE = useCallback(async()=>{
    if(applicationDetail){
      try{
        const proposedOwnersUinfin = applicationDetail?.applicationStep1?.household?.incoming_owners.map((item:any) => {
          return {
            uinfin: PolymerCrypto.encryption(item.myinfo.uinfin),
            dob: item.myinfo.dob,
          }
        });

        const proposedOccupiersUinfin = applicationDetail?.applicationStep1?.household?.incoming_occupiers.map((item:any) => {
          return {
            uinfin: PolymerCrypto.encryption(item.myinfo.uinfin),
            dob: item.myinfo.dob,
          }
        });
        const response = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(`${API_HLEStatus}`, {
          uinfin: PolymerCrypto.encryption(loginUser?.uinfin) ?? "",
          owners: proposedOwnersUinfin,
          occupiers: proposedOccupiersUinfin
        });
        
        setHasHLE(response.data.valid_hle);
      }catch(error){
        setHasHLE(false);
        }
    }
    else{
      setHasHLE(false);
    }
  },[applicationDetail, loginUser?.uinfin, singpassToken])

  

  const isMainApplicant = useMemo(()=>{
    if(applicationDetail){
      return applicationDetail.applicationStart.selected_hdb_property.main_applicant_uinfin === loginUser.uinfin;
    }
  },[applicationDetail, loginUser.uinfin])


  const OneTimeCTACall = useCallback((alert_code:string)=>async()=>{
    setIsOneTimeDisplay(false);

    const seenBySet = new Set<string>(firstApplicationStatus?.seen_by);
    seenBySet.add(loginUser.uinfin)

    const encryptedSeenByList: string[] = []
    seenBySet.forEach(seenBy => {
      encryptedSeenByList.push(seenBy)
    })
    
    try{
      let payload = {
        "application_uuid": application_id,
        "code": alert_code,
        "seen_by": encryptedSeenByList,
      }
      
      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(`${API_UpdateNotification}`,payload);
    }catch(e){
      console.log("application/notification failed", e);
    }
  },[application_id, loginUser.uinfin, singpassToken, firstApplicationStatus])

  const stageDRouteChecking = useCallback((application_status: ApplicationStatusEnum, application_uuid: string, polymer_status: PolymerStatusEnum | undefined) =>{
    if(application_status === "SENT_TO_BIOME"){
      //D1
      return `/application-coo/stage-d/hdb-review?application_id=${application_uuid}`;
    }
    else if(polymer_status){
      //NOTE: We checking using polymerStatus is because D2 and D3 next button will update the polymerStatus, So that they will go through all the D2 and D3 regardless of application status.
      // The scenario when prepare_for_completion status is assigned but applicant haven't completely go through D2 and D3 yet.

      if((polymer_status === "D3") && (application_status === "REVIEW_FINANCIAL_PLAN" || application_status === "PREPARE_FOR_COMPLETION")){
        return `/application-coo/stage-d/review-financial-plan?application_id=${application_uuid}`;
      }
      else{
        return `/application-coo/stage-d/in-principle-approval?application_id=${application_uuid}`;
      }
    }
    else if(application_status === "APPROVE"){//In case polymerStatus is empty when in STAGE D, then use back application_status
        return `/application-coo/stage-d/in-principle-approval?application_id=${application_uuid}`;
      }
      else if(application_status === "REVIEW_FINANCIAL_PLAN"){
        return `/application-coo/stage-d/review-financial-plan?application_id=${application_uuid}`;
      }
      else{
        return "";
      }
  },[])

    

  const getApplicationDetail = useCallback(async (application: StatusUpdateInterface)=>{
    const application_uuid = application.application_id;
    const application_status = application.application_status;

    await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).get(application_status  === "SAVE_DRAFT" ? `${API_DraftDetail}?applicationUuid=${application_uuid}` :  `${API_ApplicationDetail}?applicationUuid=${application_uuid}`).then(res => {
      if (res.status === 200) {
        let obj = generatedDraftObj(res.data);
        setApplicationDetail(obj);
      }
      else if(res.status === 401){
        console.log("GET ALERT - 401, RESET AUTH");
        AppDispatch(resetAuthData());
      }
      else {
        return false;
      }
    }).catch(e => {
      console.log("Oops, something wrong, please try again later");
      return false;
    });
  },[AppDispatch, generatedDraftObj, loginUser?.uinfin, singpassToken])


  const performMapDataAndNavigate = useCallback((application: StatusUpdateInterface, stage:"A"|"B"|"C"|"D"|"E") => async (event:any)=>{
    event.preventDefault();
    const application_uuid = application.application_id;
    const application_status = application.application_status;
    const polymer_status = application.polymer_status;

    await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).get(application_status  === "SAVE_DRAFT" ? `${API_DraftDetail}?applicationUuid=${application_uuid}` :  `${API_ApplicationDetail}?applicationUuid=${application_uuid}`).then(res => {
      if (res.status === 200) {
        let property = properties.find(item => item.hdb_ref === application.hdb_ref);
        if(property){
          property = new Properties({...property});
          property.decryptOwnersOccupiersAdditionalRoles();
        }
        performDraftMapping({...res.data}, undefined, undefined, property);

        AppDispatch(setApplicationId(application_uuid));
        switch(stage){
          case "A":
            navigate(`/application-coo?application_id=${application_uuid}`);
            break;
          case "B":
            navigate(`/application-coo/stage-b/review?application_id=${application_uuid}`)
            break;
          case "C":
            navigate(`/application-coo/stage-c/payment?application_id=${application_uuid}`);
            break;
          case "D":
            AppDispatch(openRouteStageD(true));
            // navigate(stageDRouteChecking(application_status, application_uuid, polymer_status));//not working, will crash
            window.location.href = `/transfer${stageDRouteChecking(application_status, application_uuid, polymer_status)}`;
            break;
          case "E":
            navigate(`/application-coo/stage-e/prepare-for-completion?application_id=${application_uuid}`);
            break;
        }
      }
      else if(res.status === 401){
        console.log("GET ALERT - 401, RESET AUTH");
        AppDispatch(resetAuthData());
      }
      else {
        return false;
      }
    }).catch(e => {
      console.log("Oops, something wrong, please try again later");
      return false;
    });
    
  },[AppDispatch, loginUser?.uinfin, navigate, performDraftMapping, singpassToken, stageDRouteChecking, properties])

  const handleNavigation = useCallback((getDashboardAlertCode: GetDashboardCodeProps) => () => {
      PolymerAxios(loginUser?.uinfin ?? "", singpassToken).get(`${API_ApplicationDetail}?applicationUuid=${getDashboardAlertCode.application_id}`).then((res) => {
        let cancellationReason = "";
        switch(getDashboardAlertCode.code){
            case "UNCNoSubmission":
              cancellationReason = "overdue for application submission";
              break;
            case "UNCIncompleteEndorse":
              cancellationReason = "overdue for consents and endorsements";
              break;
            case "UNCNoPayment":
              cancellationReason = "overdue for payment";
              break;
            case "UNCNoDocument":
              cancellationReason = "overdue for supporting documents upload"
              break;
            default: 
              cancellationReason = res.data?.cancellation_reason ?? getDashboardAlertCode.cancellation_reason;
              break;
        }
        AppDispatch(setApplicationDetails({...res.data, cancellation_reason: cancellationReason, code: getDashboardAlertCode.code}));
        
        AppDispatch(setCancelledBy(getDashboardAlertCode.code === "UNCApplicant" ? "owner" : "officer"));
        navigate(`/application-coo/application-cancelled`);
      })
  }, [AppDispatch, loginUser?.uinfin, navigate, singpassToken])
  


  // const checkIsGivenConsent = useCallback(async (application_id:string)=>{
  //   const response = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).get(`${API_CheckApplicationConsent}?applicationUuid=${application_id}`).then(res=>{
  //     const data = res.data;
  //     const result:OwnerConsentStatus[] = data.owners_consent_status;
  //     const foundOwner = result.find(e=>e.uin_fin === loginUser.uinfin);
  //     return foundOwner ? foundOwner.consent_given : true;
  //   }).catch(e=>{
  //     return true;
  //   });
  //   return response;

  // },[loginUser.uinfin, singpassToken])


  
  //Code define here https://docs.google.com/spreadsheets/d/1Cr3m3SOuCDBm_AVmXgg6wFz2V9KznYua/edit#gid=629540215
  const getDashboardAlertCode = useMemo(():GetDashboardCodeProps=>{

    let code = null;
    let application_id = "";
    let application_create_date = "";
    let last_updated_at = "";
    let gather_consent_complete_date = "";
    let payment_date = "";
    let alert_code = "";
    let updated_by = "";
    let cancellation_reason = "";
    let due_date = "";
    let request_date = "";

    
    const applications = sortedOngoingApplications ?? [];
    if(applications?.length > 1){
      code = 'Multiple';
    }
    else if(applications?.length === 1){
      const currentDate = moment();
      const ongoing_application = applications[0];
      application_id = ongoing_application?.application_id;
      application_create_date = ongoing_application?.application_create_date;
      last_updated_at = ongoing_application?.submitted_date || ongoing_application?.last_updated_at || application_create_date; //submitted date;
      gather_consent_complete_date = ongoing_application?.gather_consent_complete_date || application_create_date; //submitted date;
      payment_date = ongoing_application?.payment_date || application_create_date; //submitted date;
      alert_code = ongoing_application?.code || "";
      updated_by = ongoing_application?.updated_by || "";
      cancellation_reason = ongoing_application?.cancellation_reason || "";


      if(alert_code){
        console.log("DEBUG -- DASHBOARD alert_code",alert_code);
        setIsOneTimeDisplay(true);
        code = alert_code;
      }
      else{
        switch(ongoing_application?.application_status){
          case "SENT_TO_BIOME":
          case "PENDING_APPLICANT_HDB_REVIEW":
          case "HDB_REVIEW":
            if(!StageDData || (StageDData.message_info.length === 0 && StageDData.folders.length === 0 && (!StageDData?.solicitor_message_info || (StageDData?.solicitor_message_info && StageDData?.solicitor_message_info.length === 0)))){
              break;
            }
            const requestsKeyDates = StageDData.message_info
            .map((item) => {
              return {
                received_date: item.received_date,
                due_date: item.due_date,
                type: "message"
              };
            })
            .concat(
              StageDData?.solicitor_message_info
                ? StageDData?.solicitor_message_info.map((item) => {
                    return {
                      received_date: item.received_date,
                      due_date: item.due_date,
                      type: "solicitor_message"
                    };
                  })
                : []
            )
            .concat(
              StageDData.folders.map((item) => {
                return {
                  received_date: item.received_date,
                  due_date: item.due_date,
                  type: "document"
                };
              })
            );
            let daysDifference: number = -1;
            let urgentRequestType: string = "";
            for(const keyDate of requestsKeyDates){
              const lapsedDuration = moment(currentDate).diff(moment(keyDate.received_date), "days");
              if(lapsedDuration > daysDifference){
                  daysDifference = lapsedDuration;
                  urgentRequestType = keyDate.type;
                  request_date = keyDate.received_date;
                  due_date = keyDate.due_date;
              }
            }
            if(urgentRequestType === "document" || urgentRequestType === "message"){
              code = "UNRequestDocument";
              if(daysDifference >= 12 ){
                code = "UNRRequestDocument";
              }
              if(daysDifference > 14 ){
                code = "UNExtendDue";
              }
              if(daysDifference > 24 ){
                code = "UNRExtendDue";
              }
            }
            else if(urgentRequestType === "solicitor_message"){
              code = "UNLawyer";
              if(daysDifference > 24 ){
                code = "UNR2Lawyer";
              }
            }
            break;
          case "APPROVE":
            code = "UNApprove";
            break;
          default:
             switch(ongoing_application?.stages_status){
              case StagesStatusEnum.draft:
                if(hasHLE){
                  code = "UNHLEDraft";
                }
                else{
                  code = "UNDraft";
                }
                break;
              case StagesStatusEnum.pending_consent:
                if(moment(currentDate).format('yyyy-MM-DD') > moment(last_updated_at).add({"days" : 3}).format('yyyy-MM-DD')){
                  code = "UNREndorsement";
                }else{
                  code = "UNEndorsement";
                }
                break;
              case StagesStatusEnum.pending_payment:
                let daysDifference = currentDate.diff(gather_consent_complete_date, 'days');
                code = "UNPayment";
                if(daysDifference > 3 ){
                  code = "UNRPayment";
                }
                break;
              default:
                break;
            }
          break;
        }
      }
    }


    return { code: code as DashboardCode, application_id, application_create_date, last_updated_at, gather_consent_complete_date, payment_date, alert_code, updated_by, cancellation_reason, due_date: due_date, request_date: request_date };

    
  },[hasHLE, isMainApplicant, sortedOngoingApplications, StageDData])

  const getAlertVariant= useCallback(async (applications:StatusUpdateInterface[], performMapDataAndNavigate:Function)=>{
    const stageAorB = applications[0].stages_status === StagesStatusEnum.draft
      ? "A"
      : "B";

    switch(getDashboardAlertCode.code){
      case "Multiple":
        if(applications?.length !== 0){
          return <P2Alert variant="warning" withCTA title={`There are ${applications?.length} items pending your action.`} text="Please proceed to complete the items pending your actions in order for HDB to continue processing your application." linkText="View pending items" link="#" onClick={performMapDataAndNavigate(applications[0],stageAorB)}/>
        }
       return <></>
      case "UNDraft":
        return <P2Alert variant="warning" withCTA title="Continue with your Change in Flat Ownership application." text={`You have a pending draft application for a change in flat ownership. Please complete your application by ${Helpers.formatDate(moment(getDashboardAlertCode?.application_create_date.toString()).add({days: expiryDuration.draft}).format("yyyy-MM-DD"))} or the application will be cancelled. `} linkText="Continue Application" link={`/transfer/application-coo?application_id=${getDashboardAlertCode.application_id}`}/>
      case "UNHLEDraft":
        return <P2Alert variant="warning" withCTA title="Continue with your Change in Flat Ownership application." text={`You have a pending draft application for a change in flat ownership. Please complete your application by ${Helpers.formatDate(moment(getDashboardAlertCode?.application_create_date.toString()).add({days: expiryDuration.hledraft}).format("yyyy-MM-DD"))} or the application will be cancelled. `} linkText="Continue Application" link={`/transfer/application-coo?application_id=${getDashboardAlertCode.application_id}`}/>
      case "UNEndorsement":
        return <P2Alert variant="warning" withCTA title="Proceed to give your consent." text={`All current and proposed owners are required to review and provide their consent for the change in flat ownership application. Please inform the other applicants to provide their consent by  ${Helpers.formatDate(moment(getDashboardAlertCode?.last_updated_at.toString()).add({days: expiryDuration.endorsement}).format("yyyy-MM-DD"))} or the application will be cancelled.`} linkText="Give Consent" link={`/transfer/application-coo?application_id=${getDashboardAlertCode.application_id}`}/>
      case "UNREndorsement":
        return <P2Alert variant="warning" withCTA title="Proceed to give your consent." text={`All current and proposed owners have yet to review and provide their consent for the change in flat ownership application. Please inform the other applicants to provide their consent by ${Helpers.formatDate(moment(getDashboardAlertCode?.last_updated_at.toString()).add({days: expiryDuration.endorsement}).format("yyyy-MM-DD"))}  or the application will be cancelled.`} linkText="Give Consent" link={`/transfer/application-coo?application_id=${getDashboardAlertCode.application_id}`}/>
      case "UNPayment":
        return <P2Alert variant="warning" withCTA title="Proceed to payment." text={`All applicants have provided their consent. Please make payment to complete the application process.`} linkText="Make Payment" link={`#`} onClick={performMapDataAndNavigate(applications[0],"C")}/>
      case "UNRPayment":
        return <P2Alert variant="warning" withCTA title="Proceed to payment." text={`Please make the necessary payment by ${Helpers.formatDate(moment(getDashboardAlertCode?.gather_consent_complete_date.toString()).add({days: expiryDuration.payment}).format("yyyy-MM-DD"))} to complete the application process. The application will be cancelled if payment is not received by the due date. `} linkText="Make Payment" link={`#`} onClick={performMapDataAndNavigate(applications[0],"C")}/>
      case "UNRequestDocument":
        return <P2Alert variant="warning" withCTA title="Proceed to upload your documents." text={`HDB has requested for submission of the documents required. Please submit and respond by ${Helpers.formatDate(getDashboardAlertCode?.due_date)} or the application will be cancelled.`} linkText="Upload Document" link={`#`} onClick={performMapDataAndNavigate(applications[0],"D")}/>
      case "UNRRequestDocument":
        return <P2Alert variant="warning" withCTA title="Proceed to upload your documents." text={`HDB has requested for submission of the documents required. As HDB has yet to receive your response, please submit the documents by ${Helpers.formatDate(getDashboardAlertCode?.due_date)}. Otherwise, the application will be cancelled automatically thereafter and fees will not be refunded.`} linkText="Upload Document" link={`#`} onClick={performMapDataAndNavigate(applications[0],"D")}/>
      case "UNExtendDue":
        return <P2Alert variant="warning" withCTA title="Your application is overdue." text={`HDB has not received your response on ${Helpers.formatDate(moment(getDashboardAlertCode?.request_date?.toString()).add({days: expiryDuration.hdb_review}).format("yyyy-MM-DD"))}. We have extended the application due date to ${Helpers.formatDate(getDashboardAlertCode?.due_date)}. Please submit the required response or the application will be cancelled. Any fees paid will not be refunded.`} linkText="Upload Document" link={`#`} onClick={performMapDataAndNavigate(applications[0],"D")}/>
      case "UNRExtendDue":
        return <P2Alert variant="warning" withCTA title="Your application is overdue." text={`HDB has not received your response since ${Helpers.formatDate(moment(getDashboardAlertCode?.request_date?.toString()).format("yyyy-MM-DD"))}. We have extended the application to ${Helpers.formatDate(getDashboardAlertCode?.due_date)}. No further extension will be given, and the application will be cancelled by ${Helpers.formatDate(getDashboardAlertCode?.due_date)}. Any fees paid will not be refunded. You will need to apply again and pay the necessary fees when ready. Please submit the required response by ${Helpers.formatDate(getDashboardAlertCode?.due_date)}. `} linkText="Upload Document" link={`#`} onClick={performMapDataAndNavigate(applications[0],"D")}/>
      case "UNLawyer":
        return <P2Alert variant="warning" withCTA title="Your application requires the engagement of a private lawyer." text={`After careful consideration, HDB is unable to represent you as the lawyer for the application. Please engage a private lawyer who is in a better position to assist you with this matter and submit their representation letter.`} linkText="Provide Private Lawyer Information" link={`#`} onClick={performMapDataAndNavigate(applications[0],"D")}/>
      case "UNR2Lawyer":
        return <P2Alert variant="warning" withCTA title="Your application is overdue and requires the engagement of a private lawyer." text={`After careful consideration, HDB is unable to represent you as the lawyer for the application. Please engage a private lawyer who is in a better position to assist you with this matter and submit their representation letter. As HDB has not received your response on ${Helpers.formatDate(moment(getDashboardAlertCode?.request_date?.toString()).add({days: expiryDuration.hdb_review}).format("yyyy-MM-DD"))}, we have extended the application due date to ${Helpers.formatDate(getDashboardAlertCode?.due_date)}.`} linkText="Provide Private Lawyer Information" link={`#`} onClick={performMapDataAndNavigate(applications[0],"D")}/>
    }

    if(isOneTimeDisplay && getDashboardAlertCode.alert_code){
      if (firstApplicationStatus?.seen_by?.includes(loginUser.uinfin)) return null;
      
      switch(getDashboardAlertCode.code){
        case "UNCNoSubmission":
          return <P2Alert variant="warning" title="Your application has been cancelled." text={`Your draft application for change in flat ownership has been cancelled as it was not submitted within the given time. `} withCTA linkText="Dismiss" link={`#`} onClick={OneTimeCTACall(getDashboardAlertCode.alert_code)}/>
        case "UNCIncompleteEndorse":
          return <P2Alert variant="warning" title="Your application has been cancelled." text={`Your draft application for change in flat ownership has been cancelled as not all owners have provided their consent and endorsement.`}/>
        case "UNCNoPayment":
          return <P2Alert variant="warning" title="Your application has been cancelled." text={`Your application for change in flat ownership has been cancelled as the payment has not been received by the due date.`}/>
        case "UNCNoDocument":
          return <P2Alert variant="warning" title="Your application has been cancelled." text={`Your submitted application for change in flat ownership has been cancelled as we did not receive the supporting documents requested on ${stageDRequestedDate}. Any fees paid will not be refunded. You will need to re-apply again and pay the necessary fees when ready.`} onClick={OneTimeCTACall(getDashboardAlertCode.alert_code)}/>
        case "UNCApplicant":
          return <P2Alert variant="warning" title="Your application has been cancelled." text={`Your application for change in flat ownership has been cancelled by ${getDashboardAlertCode.updated_by} on ${getDashboardAlertCode.last_updated_at}. Any fees paid are non-refundable.`} withCTA linkText="View details" link={`#`} onClick={handleNavigation(getDashboardAlertCode)}/>
        case "UNCHDB":
          return <P2Alert variant="warning" title="Your application has been cancelled." text={`Your application for change in flat ownership has been cancelled on ${getDashboardAlertCode.last_updated_at}. Any fees paid are non-refundable.`} withCTA linkText="View details" link={`#`} onClick={handleNavigation(getDashboardAlertCode)}/>
        case "UNReject":
          return <P2Alert variant="warning" title="Application rejected by HDB." text={`Your application has been rejected by HDB.`} withCTA linkText="View details" link={`#`} onClick={handleNavigation(getDashboardAlertCode)}/>
        case "UNApprove":
          return (
            <P2Alert
              variant="success"
              title="In-Principal Approval has been granted"
              text="Your application has been approved by HDB."
              withCTA
              linkText="View details"
              link={`#`}
              onClick={(event: any) => {
                OneTimeCTACall(getDashboardAlertCode.alert_code)();
                performMapDataAndNavigate(applications[0], "D")(event);
              }}
            />
          );
      }
    }


  },[OneTimeCTACall, getDashboardAlertCode.alert_code, getDashboardAlertCode?.application_create_date, getDashboardAlertCode.application_id, getDashboardAlertCode.cancellation_reason, getDashboardAlertCode.code, getDashboardAlertCode?.gather_consent_complete_date, getDashboardAlertCode.last_updated_at, getDashboardAlertCode?.payment_date, getDashboardAlertCode.updated_by, isOneTimeDisplay, stageDRequestedDate])



  const processAlertJSX = useCallback(()=>{
    if (process.env.REACT_APP_DASHBOARD_ALERT === "true") {
      getAlertVariant(sortedOngoingApplications?.filter(e => e.stages_status || e.application_status) ?? [], performMapDataAndNavigate).then(res => {
        setAlertJSX(
          <div className="mt-4">
            {res}
          </div>
        );
      });
    }
  },[getAlertVariant, sortedOngoingApplications, performMapDataAndNavigate])

  useEffect(()=>{
    let timeoutId: number;
    timeoutId = window.setTimeout(() => {
      if(firstApplicationStatus){
        getApplicationDetail(firstApplicationStatus)
      }
    }, 1000);
    return () => {
      clearTimeout(timeoutId);
    }
  
  },[firstApplicationStatus, getApplicationDetail])


  useEffect(()=>{
    if(applicationDetail){
      checkHLE();
      processAlertJSX();
    }
  },[applicationDetail, checkHLE, processAlertJSX])

  return { ongoingApplications: sortedAllApplications , alertJSX }
}

export default useGetDashboardAlert

